// BrandLogo
//
// The "horizontal logo" surface on auth-flow screens (Login / Register /
// Onboarding). Renders the active partner's logo when one is resolvable;
// falls back to Eleplan's `horizontal_logo.png` asset when no partner
// context is in play.
//
// Sources, in priority order:
//
//   1. `whitelabelOverride` — caller-supplied object with brand_logo /
//      account_id. Used by RegisterScreen during the deep-link referral
//      preview, BEFORE the user is authenticated and before redux has a
//      session — so we can show the partner brand on the register form
//      itself instead of waiting for post-auth state.
//
//   2. state.partners.partner — when the signed-in account IS a partner.
//
//   3. state.session.account.whitelabel — when a sponsored customer is
//      signed in (computed server-side via resolvePartnerForCustomer).
//
//   4. Eleplan's static horizontal_logo.png — final fallback.
//
// The rendering strategy mirrors PartnerLogo's chain (brand_logo data URL
// → /partners/logo/{account_id} → logo.dev → initials), but here we
// short-circuit straight to the Eleplan asset when no partner is in
// scope at all (rather than rendering an initials avatar — Eleplan IS
// the default brand and a generic monogram on the login screen would
// confuse users).

import React, { useMemo, useState } from 'react';
import { Image, type ImageSourcePropType, type ImageStyle, type StyleProp } from 'react-native';
import { useSelector } from 'react-redux';
import config from '../config';
import type { RootState } from '../store';

interface WhitelabelInput {
  account_id?: string | null;
  brand_logo?: string | null;
  brand_primary?: string | null;
  company_name?: string | null;
  email?: string | null;
}

interface Props {
  /**
   * Optional override — used during registration / referral-preview where
   * we have partner branding info from /partners/referral/verify but no
   * authenticated session yet. Wins over the redux sources when supplied.
   */
  whitelabelOverride?: WhitelabelInput | null;
  /** Static Eleplan asset to fall back to when no partner is in scope. */
  fallback: ImageSourcePropType;
  style?: StyleProp<ImageStyle>;
  resizeMode?: 'contain' | 'cover' | 'stretch' | 'center';
  accessibilityLabel?: string;
}

type Stage = 'inline' | 'partners-api' | 'logo-dev' | 'fallback';

function deriveDomain(wl: WhitelabelInput): string | null {
  if (wl.email && wl.email.includes('@')) {
    const domain = wl.email.split('@')[1]?.trim().toLowerCase();
    if (domain && /^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain)) return domain;
  }
  if (wl.company_name) {
    const cleaned = wl.company_name.trim().toLowerCase();
    if (/^[a-z0-9.-]+\.[a-z]{2,}$/.test(cleaned)) return cleaned;
  }
  return null;
}

function buildPartnersApiUrl(accountId: string): string {
  const base = config.partnersApi.endpoint.replace(/\/$/, '');
  return `${base}/logo/${encodeURIComponent(accountId)}`;
}

export default function BrandLogo({
  whitelabelOverride,
  fallback,
  style,
  resizeMode = 'contain',
  accessibilityLabel,
}: Props) {
  const partner = useSelector((s: RootState) => s.partners.partner);
  const customerWhitelabel = useSelector(
    (s: RootState) => (s.session as any).account?.whitelabel,
  );
  const hostOverride = useSelector((s: RootState) => s.session.whitelabelOverride);
  const userEmail = useSelector(
    (s: RootState) => (s.authentication as any).user?.email,
  );

  // Resolve the active whitelabel. Prop override → partner row → customer
  // whitelabel → pre-auth host branding → null. The shape gets normalised so
  // the rest of the component doesn't care which source it came from. The
  // host override sits below the authed sources so a signed-in user always
  // sees their own context; it only paints the login/register screens.
  const wl: WhitelabelInput | null = useMemo(() => {
    if (whitelabelOverride && (whitelabelOverride.brand_logo || whitelabelOverride.account_id)) {
      return whitelabelOverride;
    }
    if (partner) {
      return {
        account_id: (partner as any).account_id,
        brand_logo: (partner as any).brand_logo,
        company_name: (partner as any).company_name,
        email: userEmail,
      };
    }
    if (customerWhitelabel && (customerWhitelabel.image || customerWhitelabel.account_id)) {
      // Customer-side whitelabel uses `image` for the logo blob; map it
      // onto our shared shape so the fallback chain treats it like
      // partner.brand_logo.
      return {
        account_id: customerWhitelabel.account_id,
        brand_logo: customerWhitelabel.image,
        company_name: customerWhitelabel.company,
        email: userEmail,
      };
    }
    if (hostOverride && (hostOverride.brand_logo || hostOverride.company_name)) {
      // Pre-auth host branding (<subdomain>.eleplan.com). The public lookup
      // returns the logo inline in brand_logo (no account_id), so this flows
      // straight to the `inline` stage; company_name backs the logo.dev
      // fallback if there's no logo on file.
      return {
        brand_logo: hostOverride.brand_logo,
        company_name: hostOverride.company_name,
      };
    }
    return null;
  }, [whitelabelOverride, partner, customerWhitelabel, hostOverride, userEmail]);

  const initialStage: Stage = useMemo(() => {
    if (!wl) return 'fallback';
    if (wl.brand_logo) return 'inline';
    if (wl.account_id) return 'partners-api';
    if (deriveDomain(wl)) return 'logo-dev';
    return 'fallback';
  }, [wl]);
  const [stage, setStage] = useState<Stage>(initialStage);

  React.useEffect(() => {
    setStage(initialStage);
  }, [initialStage]);

  // No partner in scope → static Eleplan asset.
  if (stage === 'fallback' || !wl) {
    return (
      <Image
        source={fallback}
        style={style}
        resizeMode={resizeMode}
        accessibilityLabel={accessibilityLabel}
      />
    );
  }

  const advance = (next: Stage) => () => setStage(next);

  if (stage === 'inline' && wl.brand_logo) {
    return (
      <Image
        source={{ uri: wl.brand_logo }}
        style={style}
        resizeMode={resizeMode}
        accessibilityLabel={accessibilityLabel || wl.company_name || undefined}
        onError={advance(wl.account_id ? 'partners-api' : (deriveDomain(wl) ? 'logo-dev' : 'fallback'))}
      />
    );
  }

  if (stage === 'partners-api' && wl.account_id) {
    return (
      <Image
        source={{ uri: buildPartnersApiUrl(wl.account_id) }}
        style={style}
        resizeMode={resizeMode}
        accessibilityLabel={accessibilityLabel || wl.company_name || undefined}
        onError={advance(deriveDomain(wl) ? 'logo-dev' : 'fallback')}
      />
    );
  }

  if (stage === 'logo-dev') {
    const domain = deriveDomain(wl);
    const token = config.logo?.devToken;
    if (domain && token) {
      return (
        <Image
          source={{ uri: `https://img.logo.dev/${domain}?token=${token}&size=256&format=png` }}
          style={style}
          resizeMode={resizeMode}
          accessibilityLabel={accessibilityLabel || wl.company_name || undefined}
          onError={advance('fallback')}
        />
      );
    }
  }

  return (
    <Image
      source={fallback}
      style={style}
      resizeMode={resizeMode}
      accessibilityLabel={accessibilityLabel}
    />
  );
}
